import React, { Fragment, useState, useEffect, useContext } from 'react'
import { Row, Col, Card, CardBody, Button } from 'reactstrap'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import { HeaderContext } from './../../context/headerContext'
import { validateOperationApi, finishOperationApi } from './../../api/operationManagemment'

function formatDateTime(value) {
    const d = new Date(value)

    if (isNaN(d.getTime())) {
        return { time: '--:--', date: '--' }
    }

    return {
        time: d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        date: d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    }
}

function OperationCardList({ fetchOperations, statusColor, orderDirection, status, onChanged, refreshTrigger }) {
    const navigate = useNavigate()
    const { selectedCitiesContext, selectedDateContext, selectedFiltersContext } = useContext(HeaderContext)

    const [operations, setOperations] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [actingOnId, setActingOnId] = useState(null)

    useEffect(() => {
        getOperations()
        // eslint-disable-next-line
    }, [selectedCitiesContext, selectedDateContext, selectedFiltersContext, refreshTrigger])

    const getOperations = async () => {
        try {
            setIsLoading(true)
            setHasError(false)

            const response = await fetchOperations(
                selectedCitiesContext.startCityName,
                selectedCitiesContext.arrivedCityName,
                selectedDateContext.SelectedFromDate,
                selectedDateContext.SelectedToDate,
                selectedFiltersContext.selectedModele,
                selectedFiltersContext.selectedConducteur
            )

            if (response && !Array.isArray(response)) {
                setHasError(true)
                setOperations([])
                return
            }

            setOperations(Array.isArray(response) ? response : [])
        }
        catch(err) {
            setHasError(true)
            setOperations([])
        }
        finally {
            setIsLoading(false)
        }
    }

    const handleCardClick = (from, to) => {
        if (!from?.lat || !from?.lng || !to?.lat || !to?.lng) return

        orderDirection({
            startPosition: { lat: from.lat, lng: from.lng },
            endPosition: { lat: to.lat, lng: to.lng },
        })
    }

    const handleEdit = (id) => navigate('/admin/EditeGestionOP/' + id)
    const handleMissionOrder = (id) => navigate('/admin/missionOrder/' + id)

    const handleValidate = async (id) => {
        setActingOnId(id)

        try {
            const response = await validateOperationApi(id)

            if (!response) {
                return Swal.fire({ title: 'Erreur!', text: 'Server error', icon: 'error' })
            }

            if (onChanged) onChanged()
            else getOperations()
        }
        catch(err) {
            Swal.fire({ title: 'Erreur!', text: err.response?.data?.error || 'Server error', icon: 'error' })
        }
        finally {
            setActingOnId(null)
        }
    }

    const handleFinish = async (id) => {
        setActingOnId(id)

        try {
            const response = await finishOperationApi(id)

            if (!response) {
                return Swal.fire({ title: 'Erreur!', text: 'Server error', icon: 'error' })
            }

            if (onChanged) onChanged()
            else getOperations()
        }
        catch(err) {
            Swal.fire({ title: 'Erreur!', text: err.response?.data?.error || 'Server error', icon: 'error' })
        }
        finally {
            setActingOnId(null)
        }
    }

    if (isLoading) {
        return (
            <Fragment>
                <p className='tabStateMessage'>Chargement des trajets…</p>
            </Fragment>
        )
    }

    if (hasError) {
        return (
            <Fragment>
                <p className='tabStateMessage error'>Impossible de charger les trajets. Vérifiez votre connexion et réessayez.</p>
            </Fragment>
        )
    }

    if (operations.length === 0) {
        return (
            <Fragment>
                <p className='tabStateMessage'>Aucun trajet ne correspond à ces filtres.</p>
            </Fragment>
        )
    }

  return (
    <Fragment>
        <section className='leftSideTabContent'>
            {operations.map((el) => {
                const from = formatDateTime(el.SelectedFromDate)
                const to = formatDateTime(el.SelectedToDate)

                return (
                    <Card
                        key={el._id}
                        className='mb-2 cardStyle'
                        style={{ borderLeftColor: statusColor }}
                        onClick={() => handleCardClick(el.startCityLocation, el.arrivedCityLocation)}
                    >
                        <CardBody>
                            <Row>
                                <Col md="4">
                                    <div className='routeInfo'>
                                        <div className='routeCity'>
                                            <span className='cityName'>{el.startCityName}</span>
                                            <span className='cityTime'>{from.time} · {from.date}</span>
                                        </div>
                                        <span className='routeLine' aria-hidden='true'></span>
                                        <div className='routeCity'>
                                            <span className='cityName'>{el.arrivedCityName}</span>
                                            <span className='cityTime'>{to.time} · {to.date}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className='modeleAndConductor'>
                                        <div className='dataRow'>
                                            <span className='dataLabel'>Modèle</span>
                                            <span className='dataValue'>{el.selectedModel?.marque || '—'}</span>
                                        </div>
                                        <div className='dataRow'>
                                            <span className='dataLabel'>Conducteur</span>
                                            <span className='dataValue'>{el.selectedConducteur?.firstName} {el.selectedConducteur?.lastName}</span>
                                        </div>
                                        <div className='dataRow'>
                                            <span className='dataLabel'>Téléphone</span>
                                            <span className='dataValue mono'>{el.selectedConducteur?.phone || '—'}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className='handleOrderMission'>
                                        <span className='plate'>{el.selectedMatricule?.immatriculation || '—'}</span>
                                        <div className='actions'>
                                            <Button className='primaryAction' onClick={(e) => { e.stopPropagation(); handleEdit(el._id) }}>Modifier</Button>
                                            <Button className='secondaryAction' onClick={(e) => { e.stopPropagation(); handleMissionOrder(el._id) }}>Ordre de mission</Button>
                                            {status === 'planifie' && (
                                                <Button
                                                    className='validateAction'
                                                    disabled={actingOnId === el._id}
                                                    onClick={(e) => { e.stopPropagation(); handleValidate(el._id) }}
                                                >
                                                    {actingOnId === el._id ? 'Validation…' : 'Valider'}
                                                </Button>
                                            )}
                                            {status === 'enCours' && (
                                                <Button
                                                    className='finishAction'
                                                    disabled={actingOnId === el._id}
                                                    onClick={(e) => { e.stopPropagation(); handleFinish(el._id) }}
                                                >
                                                    {actingOnId === el._id ? 'Finalisation…' : 'Terminer'}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )
            })}
        </section>
    </Fragment>
  )
}

export default OperationCardList
